const cacheKey = (text, mode = 'sentence') => `${mode}::${String(text || '').trim()}`;

class SessionAudioCache {
  constructor() {
    this.sessionKey = '';
    this.urls = new Map();
    this.inflight = new Map();
    this.prefetchControllers = new Set();
  }

  begin(sessionKey) {
    const next = String(sessionKey || 'default');
    if (this.sessionKey === next) return;
    this.dispose();
    this.sessionKey = next;
  }

  async get(text, mode = 'sentence', { priority } = {}) {
    const normalizedText = String(text || '').trim();
    if (!normalizedText) throw new Error('Audio text is required');
    const key = cacheKey(normalizedText, mode);
    if (this.urls.has(key)) return this.urls.get(key);
    if (this.inflight.has(key)) return this.inflight.get(key);

    const controller = new AbortController();
    this.prefetchControllers.add(controller);
    const token = localStorage.getItem('token');
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text: normalizedText, mode }),
      signal: controller.signal,
    };
    if (priority) requestOptions.priority = priority;
    const request = fetch('/api/tts/say', requestOptions).then(async (response) => {
      if (!response.ok) throw new Error('Audio could not be prepared');
      const url = URL.createObjectURL(await response.blob());
      this.urls.set(key, url);
      return url;
    }).finally(() => {
      this.prefetchControllers.delete(controller);
      this.inflight.delete(key);
    });
    this.inflight.set(key, request);
    return request;
  }

  async prefetch(requests, { concurrency = 6, onProgress, priority } = {}) {
    const unique = [];
    const seen = new Set();
    (requests || []).forEach((entry) => {
      const text = String(entry?.text || '').trim();
      const mode = entry?.mode === 'word' ? 'word' : 'sentence';
      const key = cacheKey(text, mode);
      if (!text || seen.has(key)) return;
      seen.add(key);
      unique.push({ text, mode });
    });
    let cursor = 0;
    let done = 0;
    let failed = 0;
    onProgress?.({ done, total: unique.length, failed });
    const worker = async () => {
      while (cursor < unique.length) {
        const entry = unique[cursor];
        cursor += 1;
        try {
          await this.get(entry.text, entry.mode, { priority });
        } catch (_) {
          failed += 1;
        } finally {
          done += 1;
          onProgress?.({ done, total: unique.length, failed });
        }
      }
    };
    await Promise.all(Array.from(
      { length: Math.min(Math.max(1, concurrency), unique.length) },
      () => worker(),
    ));
    return { done, total: unique.length, failed };
  }

  has(text, mode = 'sentence') {
    return this.urls.has(cacheKey(text, mode));
  }

  dispose() {
    this.prefetchControllers.forEach((controller) => controller.abort());
    this.prefetchControllers.clear();
    this.inflight.clear();
    this.urls.forEach((url) => URL.revokeObjectURL(url));
    this.urls.clear();
    this.sessionKey = '';
  }
}

export const sessionAudioCache = new SessionAudioCache();

export const beginAudioSession = (key) => sessionAudioCache.begin(key);
export const getSessionAudioUrl = (text, mode) => sessionAudioCache.get(text, mode);
export const prefetchSessionAudio = (requests, options) => sessionAudioCache.prefetch(requests, options);
export const disposeAudioSession = () => sessionAudioCache.dispose();
